import { useEffect, useRef, useState } from 'react'

import { streamUrl } from '../api/client'
import type { StreamEvent } from '../api/types'

type Status = 'connecting' | 'online' | 'offline'

/**
 * Подписка на изменения инцидентов.
 *
 * Соединение восстанавливается с экспоненциальной паузой: у дежурного вкладка
 * открыта сутками, и при перезапуске API десятки клиентов не должны
 * одновременно долбиться в сокет.
 */
export function useIncidentStream(onEvent: (event: StreamEvent) => void) {
  const [status, setStatus] = useState<Status>('connecting')
  const handler = useRef(onEvent)
  handler.current = onEvent

  useEffect(() => {
    let socket: WebSocket | null = null
    let timer: number | undefined
    let attempt = 0
    let closed = false

    const connect = () => {
      const url = streamUrl()
      if (!url) {
        setStatus('offline')
        return
      }
      setStatus('connecting')
      socket = new WebSocket(url)

      socket.onopen = () => {
        attempt = 0
        setStatus('online')
      }
      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data as string) as StreamEvent
          if (event.type && event.type !== 'ping') handler.current(event)
        } catch {
          /* нераспознанный кадр игнорируем */
        }
      }
      socket.onclose = () => {
        if (closed) return
        setStatus('offline')
        const delay = Math.min(1000 * 2 ** attempt++, 30_000)
        timer = window.setTimeout(connect, delay)
      }
      socket.onerror = () => socket?.close()
    }

    connect()
    return () => {
      closed = true
      if (timer) window.clearTimeout(timer)
      socket?.close()
    }
  }, [])

  return status
}
